import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

const SelectWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
`;

const Label = styled.label`
    font-size: 14px;
    margin-bottom: 6px;
    color: #333;
`;

const InputWrapper = styled.div`
    position: relative;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 8px 30px 8px 8px; /* right padding for icon */
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
`;

const DropdownIcon = styled.span`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 12px;
    color: #666;
`;

const Dropdown = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #ccc;
    border-top: none;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    list-style: none;
    margin: 0;
    padding: 0;
`;

const Option = styled.li`
    padding: 8px;
    cursor: pointer;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const DynamicSelect = ({
    input,
    meta,
    data,
    label,
    placeholder,
    valueField,
    labelField,
    defaultOption,
    onSearch, // function to fetch new options based on input
    resetOn,
    optionsLoading,
    searchDelay, // default debounce delay
    onSelect, // callback when an option is selected
}) => {
    const [options, setOptions] = useState(data);
    const [query, setQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [localLoading, setLocalLoading] = useState(optionsLoading || false);

    // Debounced search function to avoid excessive calls
    const debouncedSearch = useRef();

    useEffect(() => {
        if (onSearch) {
            debouncedSearch.current = debounce(async (q) => {
                await onSearch(q);
            }, searchDelay);
        } else {
            debouncedSearch.current = null;
        }
    
        return () => {
            // Cancel any pending debounced calls if they exist
            if (debouncedSearch.current) {
                debouncedSearch.current.cancel();
            }
        };
    }, [onSearch, searchDelay]);
    


    const handleChange = (e) => {
        // Set local loading state to true when input changes
        setLocalLoading(true);
        // Clear the input value to avoid showing stale data
        input.onChange('');
        onSelect(null); // Reset selection on input change
        // Update the query state with the current input value
        setQuery(e.target.value);
        setShowDropdown(true);
    };

    const handleSelect = async(option) => {
        // Set the input value to the label field for display
        setQuery(option[labelField]);
        // Update the input value and close the dropdown
        await input.onChange(option[valueField]);
        setShowDropdown(false);
        // Call the onSelect callback if provided
        onSelect(option);
    };

    // Reset options when resetOn changes
    useEffect(() => {
        if (resetOn) {
            setQuery('');
            input.onChange('');
            setOptions([]);
        }
    }, [resetOn])

    useEffect(() => {
        if (defaultOption) {
            if (defaultOption[valueField] !== input.value) {
                input.onChange(defaultOption[valueField]);
            }
    
            if (defaultOption[labelField] !== query) {
                setQuery(defaultOption[labelField]);
            }
        } else {
            // Reset both input and visible query when defaultOption is null
            if (input.value !== '') input.onChange('');
            if (query !== '') setQuery('');
        }
    }, [defaultOption]);
    

    // Update options when data prop changes
    useEffect(() => {
        setOptions(data || []);
        // set local loading to false when data is set
        setLocalLoading(false);
    }, [data]);

    // Trigger search when query changes
    useEffect(() => {
        if (debouncedSearch.current) {
            debouncedSearch.current(query);
        }else{
            if(!query) setOptions(data || [])
            else {
                const updatedOptions = data.filter(option => 
                    option[labelField]?.toLowerCase().includes(query.toLowerCase())
                );
                setOptions(updatedOptions)
            }
            setLocalLoading(false);
        }
    }, [query, debouncedSearch]);

    return (
        <SelectWrapper>
            {label && <Label>{label}</Label>}
            <InputWrapper>
                <StyledInput
                    value={query}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onFocus={() => {
                        setShowDropdown(true);
                        if (onSearch) {
                            query.length === 0 && onSelect && onSelect(null)
                            options.length === 0 && debouncedSearch.current && debouncedSearch(query); // Trigger search on focus
                        }
                    }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                />
                <DropdownIcon>▼</DropdownIcon>
            </InputWrapper>
            {showDropdown && (
                <Dropdown>
                    {optionsLoading || localLoading ? (
                        <Option disabled>Loading...</Option>
                        ) : options.length === 0 ? (
                        <Option disabled>No results found</Option>
                        ) : (
                        options.map(option => (
                            <Option key={option[valueField]} onMouseDown={() => handleSelect(option)}>
                            {option[labelField]}
                            </Option>
                        ))
                    )}
                </Dropdown>
                )}
            {/* Display error message if input is touched and has an error */}
            {meta.touched && meta.error && !showDropdown && (
                <span style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {meta.error}
                </span>
            )}
        </SelectWrapper>
    );
};

DynamicSelect.propTypes = {
    input: PropTypes.object.isRequired, // input object from react-final-form or similar library
    meta: PropTypes.object, // meta information for validation and touched state from react-final-form
    data: PropTypes.array, // array of options to display in the dropdown
    label: PropTypes.string, // label for the input field
    placeholder: PropTypes.string, // placeholder text for the input field
    valueField: PropTypes.string, // field to use as the value in the select options
    labelField: PropTypes.string, // field to display in the dropdown
    onSearch: PropTypes.func, // function to fetch options based on input value
    defaultOption: PropTypes.object, // default value for the select input
    resetOn: PropTypes.any, // value to reset the select options
    optionsLoading: PropTypes.bool, // indicates if options are being loaded
    searchDelay: PropTypes.number, // debounce delay for search function
    onSelect: PropTypes.func, // callback when an option is selected
};

DynamicSelect.defaultProps = {
    data: [],
    placeholder: 'Type to search...',
    valueField: 'value',
    labelField: 'name',
    onSearch: null,
    defaultOption: null,
    resetOn: null,
    optionsLoading: false,
    searchDelay: 300, // default debounce delay
    onSelect: () => {}, // default no-op function for onSelect
};

export default DynamicSelect;
